import Vue from 'vue'
import VueRouter, { NavigationGuardNext, Route, RouteConfig } from 'vue-router'
import Layout from '@/components/layout/Layout.vue'
import { hasStoredSession } from '@/Services/sessionService'
import { usageTracker } from '../Services/usageTracker'

Vue.use(VueRouter)

/** Path of the dashboard layout parent — all authenticated app views nest under this record. */
const DASHBOARD_LAYOUT_PATH = '/dashboard'

const router = new VueRouter({
  mode: 'history',
  base: '',
  routes: [
    {
      path: '/',
      name: 'Login',
      component: () => import('@/views/LogIn.vue')
    },
    {
      path: '/otp',
      name: 'Otp',
      component: () => import('@/views/Otp.vue')
    },
    {
      path: '/reset-password',
      name: 'ResetPassword',
      component: () => import('@/views/ResetPassword.vue')
    },
    {
      path: '/dashboard',
      component: Layout,
      children: [
        {
          path: '/InvoiceStatus',
          name: 'InvoiceStatus',
          component: () => import('@/views/InvoiceStatus.vue')
        },
        {
          path: '/Billing',
          name: 'Billing',
          component: () => import('@/views/Billing.vue')
        },
        {
          path: '/NotSubmitted',
          name: 'NotSubmitted',
          component: () => import('@/views/NotSubmitted.vue')
        }
      ]
    }
  ] as RouteConfig[]
})

/**
 * Determines whether the navigation target is rendered inside the dashboard layout.
 *
 * Dashboard children use absolute paths (`/InvoiceStatus`, `/Billing`, `/NotSubmitted`)
 * so the URL does not contain the `/dashboard` prefix, but `to.matched` still includes
 * the parent layout record. This helper keys off that parent record rather than URL
 * string prefix matching.
 *
 * @param to - Destination route being resolved by vue-router.
 * @returns `true` when the route is protected by the dashboard auth guard.
 */
function isDashboardRoute(to: Route): boolean {
  return to.matched.some((record) => record.path === DASHBOARD_LAYOUT_PATH)
}

/**
 * Returns whether the browser tab is marked as holding an authenticated session.
 *
 * Q-17 (AB#3390 W7): the opaque UUID lives in an httpOnly `yango-session` cookie;
 * the router checks only the tab-scoped `yango-session-active` flag set by
 * `Otp.vue`. Validity is enforced server-side on each API call.
 */
function hasAuthenticatedSession(): boolean {
  return hasStoredSession()
}

/**
 * `beforeEach` guard that blocks unauthenticated access to dashboard views.
 *
 * Contract (T-1c / AD-3 in `docs/design-docs/2026-07-02-AB#3390-code-review-remediation-gap-analysis.md`):
 * - Protected: all routes nested under the `/dashboard` layout (`InvoiceStatus`, `Billing`,
 *   `NotSubmitted`, and the bare `/dashboard` parent).
 * - Public: `/` (Login), `/otp`, `/reset-password` — no session flag required.
 * - Missing session flag → redirect to `{ name: 'Login' }` (no `returnUrl` query;
 *   restoring deep links is out of scope for T-11).
 *
 * Assumptions:
 * - Logout clears the session flag and httpOnly cookie before navigating to Login.
 * - OTP / reset-password flows do not set the session flag until verification succeeds.
 *
 * Edge cases:
 * - Direct URL entry to `/InvoiceStatus` without a session is redirected client-side
 *   before the view mounts (closes F-2 / R-17 functional gap).
 * - Expired or revoked tokens are not detected here; API 401 handling remains in views.
 *
 * @param to - Destination route.
 * @param _from - Origin route (unused; navigation may originate from any public page).
 * @param next - vue-router resolver; must be called exactly once per invocation.
 */
function dashboardAuthGuard(
  to: Route,
  _from: Route,
  next: NavigationGuardNext
): void {
  if (isDashboardRoute(to) && !hasAuthenticatedSession()) {
    next({ name: 'Login' })
    return
  }

  next()
}

router.beforeEach(dashboardAuthGuard)

/**
 * Records a usage-telemetry event for every successful client-side navigation.
 *
 * Fires AFTER navigation has resolved, so `to` reflects the route the user is
 * actually viewing. The emitted `feature` is a static prefix joined with the
 * route's logical name (e.g. `page:InvoiceStatus`, `page:Login`) — the route
 * name is a developer-defined constant in the routes table, not user input,
 * so no sanitisation is required (C1.3 closed-enum guard is upheld).
 *
 * `action` is the closed-enum literal `'view'` (C1.3 / C4.3). The tracker
 * is fire-and-forget; failures are swallowed inside `usageTracker.track()`
 * and do not affect navigation.
 */
router.afterEach((to) => {
  // STATIC STRING — do not interpolate user input.
  const feature = 'page:' + (to.name || to.path)
  usageTracker.track(feature, { action: 'view' })
})

export default router
